import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SERVICE_UNIT_LABELS, prospectQuote, type AnnualProposal, type FeeMode, type PricedService, type ProspectLines, type ServiceKind, type ServiceUnit } from '@raro/domain';
import { Breadcrumbs, Button, Notice } from '@raro/ui';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../lib/api';
import { formatMoney } from '../../lib/format';

type Svc = { key: string; name: string; description: string; kind: ServiceKind; unit: ServiceUnit; isActive: boolean; price: { price: number; credits: number; cost: number; missing: string[] } };
type Prospect = { id: string; name: string; segment: string | null; monthlyFee: number; feeMode: FeeMode; walletCredits: number; base: ProspectLines; menu: ProspectLines; seats: ProspectLines; passthrough: number; proposal: AnnualProposal | null; status: string };

const QUARTERS = ['meses 1 a 3', 'meses 4 a 6', 'meses 7 a 9', 'meses 10 a 12'];

/**
 * A PROPOSTA DE 12 MESES, como vai para o cliente (sócios; docs/product/fee-e-carteira-v0.1.md): objetivo, diagnóstico,
 * metodologia, os quatro ciclos, o investimento (da conta da proposta: fee, MODO, repasse; nunca custo nem margem) e os
 * próximos passos. A Raro ajusta o texto antes de mandar; "imprimir" gera o PDF pelo navegador.
 */
export function PropostaPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['prospects'], queryFn: () => api.get<{ prospects: Prospect[] }>('/valor/prospects') });
  const pricing = useQuery({ queryKey: ['pricing'], queryFn: () => api.get<{ services: Svc[] }>('/valor/pricing') });
  const p = list.data?.prospects.find((x) => x.id === id) ?? null;
  const [edit, setEdit] = useState<AnnualProposal | null>(null);
  useEffect(() => { setEdit(null); }, [p?.proposal?.generatedAt]);
  const save = useMutation({ mutationFn: (proposal: AnnualProposal) => api.patch<{ prospects: Prospect[] }>(`/valor/prospects/${id}`, { proposal }), onSuccess: (d) => { qc.setQueryData(['prospects'], d); setEdit(null); } });
  if (list.isError || pricing.isError) return <div className="rr-page"><Notice tone="error">{((list.error ?? pricing.error) as Error).message}</Notice></div>;
  if (!p || !pricing.data) return <div className="rr-page rr-faint">…</div>;
  if (!p.proposal) return <div className="rr-page"><Notice>esta conta ainda não tem proposta escrita. <Link to="/valor/prospeccao">voltar à prospecção</Link></Notice></div>;
  const services = pricing.data.services.filter((s) => s.isActive);
  const priced: PricedService[] = services.map((s) => ({ key: s.key, kind: s.kind, unit: s.unit, price: s.price.price, credits: s.price.credits, cost: s.price.cost, missing: s.price.missing }));
  const q = prospectQuote(p, priced);
  const name = (k: string) => services.find((s) => s.key === k);
  const baseLines = Object.keys(p.base).length ? p.base : Object.fromEntries(services.filter((s) => s.kind === 'base').map((s) => [s.key, 1]));
  const v = edit ?? p.proposal;
  const set = (x: Partial<AnnualProposal>) => setEdit({ ...v, ...x });
  const T = ({ value, onChange, rows = 2 }: { value: string; onChange: (s: string) => void; rows?: number }) => (edit ? <textarea className="rr-budget__cell rr-proposta__edit" rows={rows} value={value} onChange={(e) => onChange(e.target.value)} /> : <p>{value}</p>);

  return (
    <>
      <div className="rr-topbar rr-proposta__chrome">
        <Breadcrumbs items={[{ label: 'Prospecção', render: (l) => <Link to="/valor/prospeccao">{l}</Link> }, { label: p.name }]} />
        <div className="rr-row" style={{ gap: 6 }}>
          {edit ? <>
            <Button size="sm" variant="primary" disabled={save.isPending} onClick={() => save.mutate(edit)}>{save.isPending ? 'salvando…' : 'salvar o texto'}</Button>
            <Button size="sm" variant="quiet" onClick={() => setEdit(null)}>cancelar</Button>
          </> : <>
            <Button size="sm" variant="quiet" onClick={() => setEdit(p.proposal)}>editar o texto</Button>
            <Button size="sm" variant="primary" onClick={() => window.print()}>imprimir / PDF</Button>
          </>}
        </div>
      </div>
      {save.isError && <div className="rr-page rr-proposta__chrome"><Notice tone="error">{(save.error as Error).message}</Notice></div>}
      <article className="rr-proposta">
        <header>
          <small>proposta Raro · {p.name}{p.segment ? ` · ${p.segment}` : ''}</small>
          {edit ? <input className="rr-budget__cell rr-proposta__edit rr-proposta__title" value={v.title} onChange={(e) => set({ title: e.target.value })} /> : <h1>{v.title}</h1>}
        </header>

        <section><h2>objetivo do ano</h2><T value={v.objective} onChange={(objective) => set({ objective })} /></section>

        <section>
          <h2>diagnóstico</h2>
          <div className="rr-proposta__grid">
            <div><h3>a marca hoje</h3><T rows={4} value={v.diagnosis.brand} onChange={(brand) => set({ diagnosis: { ...v.diagnosis, brand } })} /></div>
            <div><h3>o mercado</h3><T rows={4} value={v.diagnosis.market} onChange={(market) => set({ diagnosis: { ...v.diagnosis, market } })} /></div>
            <div><h3>a oportunidade</h3><T rows={4} value={v.diagnosis.opportunity} onChange={(opportunity) => set({ diagnosis: { ...v.diagnosis, opportunity } })} /></div>
          </div>
        </section>

        <section>
          <h2>como vamos trabalhar</h2>
          <div className="rr-proposta__grid">
            {v.methodology.map((m, i) => (
              <div key={i}>
                {edit ? <input className="rr-budget__cell rr-proposta__edit" value={m.title} onChange={(e) => set({ methodology: v.methodology.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} /> : <h3>{m.title}</h3>}
                <T rows={3} value={m.text} onChange={(text) => set({ methodology: v.methodology.map((x, j) => (j === i ? { ...x, text } : x)) })} />
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2>os 12 meses, por ciclo</h2>
          <div className="rr-proposta__cycles">
            {v.cycles.map((c, i) => {
              const upd = (x: Partial<AnnualProposal['cycles'][number]>) => set({ cycles: v.cycles.map((y, j) => (j === i ? { ...y, ...x } : y)) });
              return (
                <div key={i} className="rr-proposta__cycle">
                  <small>ciclo {c.n} · {QUARTERS[i]}</small>
                  {edit ? <input className="rr-budget__cell rr-proposta__edit" value={c.focus} onChange={(e) => upd({ focus: e.target.value })} /> : <h3>{c.focus}</h3>}
                  {edit ? <textarea className="rr-budget__cell rr-proposta__edit" rows={4} value={c.actions.join('\n')} onChange={(e) => upd({ actions: e.target.value.split('\n') })} />
                    : <ul>{c.actions.map((a) => <li key={a}>{a}</li>)}</ul>}
                  <h4>efeito esperado</h4><T value={c.effect} onChange={(effect) => upd({ effect })} />
                  <h4>como vamos medir</h4><T value={c.measure} onChange={(measure) => upd({ measure })} />
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h2>investimento</h2>
          <div className="rr-proposta__invest">
            <dl>
              <dt>fee mensal</dt><dd>{formatMoney(q.monthly.fee)}</dd>
              {q.monthly.modo > 0 && <><dt>MODO · {q.seats} assento(s){q.discount ? ` (${q.discount}% de desconto)` : ''}</dt><dd>{formatMoney(q.monthly.modo)}</dd></>}
              {q.monthly.passthrough > 0 && <><dt>mídia e terceiros (estimativa, com gestão)</dt><dd>{formatMoney(q.monthly.passthrough)}</dd></>}
              <dt className="rr-prospect__total">total por mês</dt><dd className="rr-prospect__total">{formatMoney(q.monthly.total)}</dd>
            </dl>
            <div>
              <h3>o fee inclui, todo mês</h3>
              <ul>{Object.entries(baseLines).filter(([, n]) => n > 0).map(([k, n]) => { const s = name(k); return s ? <li key={k}>{s.name}{n !== 1 ? ` × ${n}` : ''}: {s.description}</li> : null; })}</ul>
              <h3>e uma carteira por ciclo para projetos</h3>
              <p>a cada trimestre, a carteira vira as ações do ciclo, escolhidas com vocês pelo objetivo. {Object.keys(p.menu).length > 0 && <>no primeiro ciclo: {Object.entries(p.menu).filter(([, n]) => n > 0).map(([k, n]) => `${name(k)?.name ?? k}${n > 1 ? ` (${n} ${SERVICE_UNIT_LABELS[name(k)?.unit ?? 'project']})` : ''}`).join(', ')}.</>}</p>
            </div>
          </div>
        </section>

        <section>
          <h2>próximos passos</h2>
          {edit ? <textarea className="rr-budget__cell rr-proposta__edit" rows={4} value={v.nextSteps.join('\n')} onChange={(e) => set({ nextSteps: e.target.value.split('\n') })} />
            : <ol>{v.nextSteps.map((n) => <li key={n}>{n}</li>)}</ol>}
        </section>
        <footer><small>Raro · proposta preparada em {new Date(p.proposal.generatedAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}. valores válidos por 30 dias.</small></footer>
      </article>
    </>
  );
}
